import { useState } from 'react';
import { Alert, Card, Descriptions, Input, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import UserAvatar from 'components/user-avatar';
import { WatermarkLookupResult, watermarkError } from './types';

const CLASS_LABEL: Record<string, string> = {
  MANAGEMENT: 'Quản lý tổng',
  AGENCY: 'Agency',
  CREATOR: 'Creator',
  STAFF: 'Nhân viên',
  PUBLIC: 'Người dùng (fan)',
};

/** Ô tra mã dùng chung: tab Tra mã và dưới ảnh ở tab Soi ảnh. */
export const LookupBox = ({ autoFocus }: { autoFocus?: boolean }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<WatermarkLookupResult | null>(null);
  const [error, setError] = useState('');

  const search = async (raw: string) => {
    const code = raw.trim();
    if (!code) return;
    setLoading(true);
    setError('');
    try {
      const res: any = await AxiosUtils.get(API.watermarkLookup, { code });
      setResult(res.data);
    } catch (err) {
      setResult(null);
      setError(watermarkError(err));
    } finally {
      setLoading(false);
    }
  };

  const user = result?.user;
  const roles: string[] = Array.isArray(user?.roles)
    ? user!.roles
    : String(user?.roles ?? '').split(',');

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Input.Search
        size="large"
        placeholder="Mã trên ảnh, vd 3F7-K2M"
        enterButton="Tra mã"
        allowClear
        autoFocus={autoFocus}
        loading={loading}
        onSearch={search}
        style={{ maxWidth: 420 }}
      />
      {error ? <Alert type="error" showIcon message={error} /> : null}
      {result && !result.found ? (
        <Alert
          type="warning"
          showIcon
          message={result.message}
          description={result.code ? `Mã đã chuẩn hoá: ${result.code}` : undefined}
        />
      ) : null}
      {result?.found && user ? (
        <Card>
          <Space align="start" size={16} wrap>
            <UserAvatar size={56} src={user.avatar} />
            <div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {user.nickName || '(chưa đặt tên)'}
              </Typography.Title>
              <Typography.Text type="secondary">
                {user.username ? `@${user.username}` : 'Chưa có TikTok ID'} · #{user.id}
              </Typography.Text>
              <div style={{ marginTop: 8 }}>
                {user.userClass ? (
                  <Tag color="blue">{CLASS_LABEL[user.userClass] || user.userClass}</Tag>
                ) : null}
                {user.deletedAt ? (
                  <Tag color="red">Đã xoá {dayjs(user.deletedAt).format('DD/MM/YYYY')}</Tag>
                ) : null}
                {user.isAccountDisabled ? <Tag color="orange">Đang bị khoá</Tag> : null}
              </div>
            </div>
          </Space>
          <Alert
            style={{ marginTop: 16 }}
            type={result.checkValid ? 'success' : 'warning'}
            showIcon
            message={`Mã ${result.code}: ${result.message}`}
          />
          <Descriptions size="small" column={1} style={{ marginTop: 16 }}>
            <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
            <Descriptions.Item label="UID TikTok">{user.tiktokUid || '—'}</Descriptions.Item>
            <Descriptions.Item label="Vai trò">
              {roles.filter(Boolean).join(', ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Phòng ban">{user.department || '—'}</Descriptions.Item>
          </Descriptions>
        </Card>
      ) : null}
    </Space>
  );
};

const LookupTab = () => (
  <Space direction="vertical" size={16} style={{ width: '100%' }}>
    <Card>
      <Typography.Paragraph style={{ marginTop: 0 }}>
        Mỗi người có một <b>mã truy vết</b> in trên mọi màn hình, dạng{' '}
        <Typography.Text code>3F7-K2M</Typography.Text> (nằm ở dòng cuối của chữ mờ, cạnh ngày giờ).
        Nhập mã đọc được trên ảnh bị lộ để ra đúng tài khoản — kể cả tài khoản đã xoá. Gõ chữ
        thường, thiếu gạch, nhầm O/0 hay I/1 đều được.
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        3 ký tự sau gạch là ký tự kiểm tra có khoá bí mật: người ngoài không tự bịa được mã hợp lệ
        của người khác để đổ tội. Báo "không khớp" = đọc nhầm một ký tự hoặc ảnh đã bị sửa — đối
        chiếu thêm tên/TikTok ID trên ảnh.
      </Typography.Paragraph>
    </Card>
    <Card>
      <LookupBox autoFocus />
    </Card>
  </Space>
);

export default LookupTab;
